import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../api/client";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { FileUp, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";

const statusLabel = {
  pendente: "A fazer",
  em_andamento: "Em andamento",
  evidencia_enviada: "Aguardando análise",
  em_analise: "Em análise",
  executada: "Aguardando conferência",
  validada: "Concluída e aprovada",
  recusada: "Precisa de correção",
};
const evidenceLabel = {
  aguardando_validacao: "Aguardando análise",
  aprovada: "Aprovada",
  recusada: "Precisa de correção",
};
const dateLabel = (value) =>
  value
    ? new Date(String(value).slice(0, 10) + "T12:00:00").toLocaleDateString(
        "pt-BR",
      )
    : "Sem prazo definido";
const accepted = /\.(jpe?g|png|webp|pdf)$/i;

export default function PublicActions() {
  const { token } = useParams();
  const [data, setData] = useState(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const [uploads, setUploads] = useState({}),
    [sending, setSending] = useState(""),
    [query, setQuery] = useState(""),
    [filter, setFilter] = useState("open"),
    [receipt, setReceipt] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData((await api.get(`/operations/public/${token}`)).data);
    } catch (e) {
      setError(
        e.response?.data?.detail ||
          "Não foi possível abrir o portal. Peça o link atualizado à DEMoraes.",
      );
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => {
    load();
  }, [load]);
  const actions = useMemo(() => data?.actions || [], [data]);
  const visible = useMemo(
    () =>
      actions.filter(
        (a) =>
          (filter === "all" ||
            (filter === "done" && a.status === "validada") ||
            (filter === "review" &&
              ["evidencia_enviada", "em_analise"].includes(a.status)) ||
            (filter === "late" && a.deadline_state === "vencida") ||
            (filter === "open" && a.status !== "validada")) &&
          `${a.texto_original} ${a.responsavel || ""}`
            .toLowerCase()
            .includes(query.toLowerCase()),
      ),
    [actions, filter, query],
  );
  const selectFile = (actionId, file) => {
    if (file && (!accepted.test(file.name) || file.type.startsWith("video/"))) {
      toast.error("Vídeos não são aceitos. Escolha JPG, PNG, WEBP ou PDF.");
      return;
    }
    if (file?.size > 12 * 1024 * 1024) {
      toast.error("O arquivo deve ter até 12 MB.");
      return;
    }
    setUploads((p) => ({
      ...p,
      [actionId]: { ...p[actionId], file, key: crypto.randomUUID() },
    }));
  };
  const submit = async (actionId) => {
    const item = uploads[actionId] || {};
    if (!item.file) return toast.error("Escolha uma foto ou PDF primeiro.");
    setSending(actionId);
    const fd = new FormData();
    fd.append("file", item.file);
    fd.append("note", item.note || "");
    fd.append("operation_key", item.key);
    try {
      const response = await api.post(
        `/operations/public/${token}/actions/${actionId}/evidence`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setReceipt({ id: response.data.evidence_id, filename: item.file.name });
      setUploads((p) => ({ ...p, [actionId]: {} }));
      toast.success("Comprovação recebida. Aguarde a análise da DEMoraes.");
      await load();
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          "Não foi possível confirmar o envio. Tente novamente com o mesmo arquivo.",
      );
    } finally {
      setSending("");
    }
  };
  const download = async (e) => {
    try {
      const response = await api.get(
        `/operations/public/${token}/evidence/${e.id}/download`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = e.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Não foi possível baixar a comprovação.");
    }
  };
  return (
    <main className="min-h-screen bg-background px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-4xl space-y-5">
        <header className="rounded-2xl border bg-card p-5 sm:p-7">
          <div className="flex gap-3">
            <ShieldCheck className="size-8 shrink-0 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">
                DEMoraes Consultoria
              </p>
              <h1 className="text-2xl font-semibold">
                Plano de ação da sua empresa
              </h1>
              <p className="mt-1">
                {data?.client_name || "Portal de comprovações"}
              </p>
            </div>
          </div>
          <p className="mt-4 text-base">
            1. Localize a ação. 2. Envie uma foto ou PDF mostrando o que foi
            feito. 3. Acompanhe a análise aqui.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Salve este link no grupo dos responsáveis da empresa. O envio não
            conclui a ação: a DEMoraes precisa aprovar a comprovação.
          </p>
        </header>
        {error && (
          <section
            role="alert"
            className="rounded-xl border border-destructive p-5"
          >
            <p>{error}</p>
            <Button className="mt-3" onClick={load}>
              Tentar novamente
            </Button>
          </section>
        )}
        {receipt && (
          <section
            role="status"
            className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4"
          >
            <h2 className="font-semibold">Comprovação recebida</h2>
            <p className="break-words">{receipt.filename}</p>
            <p className="text-sm break-all">Protocolo: {receipt.id}</p>
            <p className="mt-2 text-sm">
              O resultado da análise aparecerá no histórico da ação.
            </p>
          </section>
        )}
        {loading && (
          <p role="status" className="p-6">
            Carregando ações…
          </p>
        )}
        {data && !error && (
          <>
            <section
              aria-label="Resumo"
              className="grid grid-cols-2 gap-3 sm:grid-cols-4"
            >
              {[
                ["Ações", actions.length],
                [
                  "Vencidas",
                  actions.filter((a) => a.deadline_state === "vencida").length,
                ],
                [
                  "Em análise",
                  actions.filter((a) =>
                    ["evidencia_enviada", "em_analise"].includes(a.status),
                  ).length,
                ],
                [
                  "Concluídas",
                  actions.filter((a) => a.status === "validada").length,
                ],
              ].map(([label, count]) => (
                <div key={label} className="rounded-xl border bg-card p-4">
                  <p className="text-sm">{label}</p>
                  <p className="text-2xl font-semibold">{count}</p>
                </div>
              ))}
            </section>
            <section className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="action-search">
                  Buscar ação ou responsável
                </Label>
                <Input
                  id="action-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ex.: proteção, treinamento, João"
                />
              </div>
              <div>
                <Label htmlFor="action-filter">Mostrar</Label>
                <select
                  id="action-filter"
                  className="h-10 w-full rounded-md border bg-background px-3"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="open">Ações em aberto</option>
                  <option value="late">Vencidas</option>
                  <option value="review">Em análise</option>
                  <option value="done">Concluídas</option>
                  <option value="all">Todas as ações</option>
                </select>
              </div>
            </section>
            {!loading && !visible.length && (
              <p className="rounded-xl border p-6">
                Nenhuma ação neste filtro.
              </p>
            )}
            {visible.map((a) => (
              <Card key={a.id}>
                <CardContent className="p-5 sm:p-6">
                  <div className="flex flex-wrap gap-2">
                    <Badge
                      variant={
                        a.deadline_state === "vencida"
                          ? "destructive"
                          : "secondary"
                      }
                    >
                      {a.deadline_state === "vencida"
                        ? "Prazo vencido"
                        : statusLabel[a.status] || "Em acompanhamento"}
                    </Badge>
                    {a.deadline_state === "vencida" && (
                      <Badge variant="outline">
                        {statusLabel[a.status] || "Em acompanhamento"}
                      </Badge>
                    )}
                  </div>
                  <h2 className="mt-4 whitespace-pre-wrap break-words text-base font-medium leading-relaxed">
                    {a.texto_original}
                  </h2>
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                    <p>
                      Prazo:{" "}
                      <strong>{dateLabel(a.next_due_at || a.prazo)}</strong>
                    </p>
                    <p>Responsável: {a.responsavel || "Não informado"}</p>
                  </div>
                  {a.evidence?.length > 0 && (
                    <details
                      className="mt-4 rounded-xl border p-4"
                      open={a.evidence.some((e) => e.status === "recusada")}
                    >
                      <summary className="cursor-pointer font-medium">
                        Histórico de comprovações ({a.evidence.length})
                      </summary>
                      <div className="mt-3 space-y-3">
                        {a.evidence.map((e) => (
                          <div
                            key={e.id}
                            className="rounded-lg bg-muted/30 p-3"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="break-all font-medium">
                                {e.filename}
                              </span>
                              <Badge
                                variant={
                                  e.status === "recusada"
                                    ? "destructive"
                                    : "outline"
                                }
                              >
                                {evidenceLabel[e.status] || "Em análise"}
                              </Badge>
                            </div>
                            <p className="mt-1 text-sm">
                              Enviado em {dateLabel(e.submitted_at)}
                            </p>
                            {e.note && (
                              <p className="mt-2 whitespace-pre-wrap break-words text-sm">
                                Sua observação: {e.note}
                              </p>
                            )}
                            {e.review_note && (
                              <p className="mt-2 whitespace-pre-wrap break-words text-sm">
                                <strong>Retorno da DEMoraes:</strong>{" "}
                                {e.review_note}
                              </p>
                            )}
                            <p className="mt-2 break-all text-sm text-muted-foreground">
                              Protocolo: {e.id}
                            </p>
                            <Button
                              className="mt-2"
                              size="sm"
                              variant="outline"
                              onClick={() => download(e)}
                            >
                              Baixar comprovação
                            </Button>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                  {a.status !== "validada" ? (
                    <section className="mt-5 rounded-xl border bg-muted/20 p-4">
                      <h3 className="font-semibold">Enviar comprovação</h3>
                      <p className="mt-2 text-sm">
                        Exemplos: foto da proteção instalada, registro de
                        treinamento ou comprovante do serviço. Envie um arquivo
                        por vez; você pode enviar outros depois.
                      </p>
                      <p className="mt-1 text-sm">
                        JPG, PNG, WEBP ou PDF · até 12 MB · PDF sem senha, até
                        30 páginas. Vídeos não são aceitos.
                      </p>
                      <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        <div>
                          <Label htmlFor={`file-${a.id}`}>
                            Foto ou documento
                          </Label>
                          <Input
                            key={uploads[a.id]?.key || `empty-${a.id}`}
                            id={`file-${a.id}`}
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp,.pdf"
                            disabled={!!sending}
                            onChange={(e) =>
                              selectFile(a.id, e.target.files?.[0])
                            }
                          />
                          {uploads[a.id]?.file && (
                            <p className="mt-2 break-all text-sm">
                              Selecionado: {uploads[a.id].file.name}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor={`note-${a.id}`}>
                            O que foi feito? (opcional)
                          </Label>
                          <Textarea
                            id={`note-${a.id}`}
                            rows={3}
                            maxLength={4000}
                            disabled={!!sending}
                            value={uploads[a.id]?.note || ""}
                            onChange={(e) =>
                              setUploads((p) => ({
                                ...p,
                                [a.id]: {
                                  ...p[a.id],
                                  note: e.target.value,
                                  key: crypto.randomUUID(),
                                },
                              }))
                            }
                            placeholder="Ex.: proteção instalada no equipamento em 05/10."
                          />
                        </div>
                      </div>
                      <Button
                        className="mt-4 w-full sm:w-auto"
                        onClick={() => submit(a.id)}
                        disabled={!!sending || !uploads[a.id]?.file}
                      >
                        {sending === a.id ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <FileUp className="size-4" />
                        )}
                        Enviar para análise
                      </Button>
                    </section>
                  ) : (
                    <p className="mt-4 flex items-center gap-2 text-sm">
                      <CheckCircle2 className="size-4 text-emerald-600" />
                      Comprovação aprovada pela DEMoraes.
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </>
        )}
        <footer className="pb-6 text-sm text-muted-foreground">
          Compartilhe este link apenas com responsáveis desta empresa.
        </footer>
      </div>
    </main>
  );
}
